import React, { useState, useEffect, memo } from "react";
import {
  TextField,
  Typography,
  Container,
  Paper,
  Checkbox,
  FormControlLabel,
  Box,
  CircularProgress,
  FormControl,
  FormLabel,
  Button,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Navbar from "../../../../globals/components/Navbar";
import { apiClient } from "../../../../api/httpClient";
import { useNavigate, useParams } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import { FixedSizeList as List } from "react-window";
import { toast } from "react-toastify";
import InputMask from "react-input-mask";
import { useSelector } from "react-redux";
import GoogleAutoCompleteField from "../../../../globals/components/forms/GoogleAutoCompleteField";
import { normalizeUsState, isAllowedUsState } from "../../../../globals/utils/usStates";

const customStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: "56px",
    border: "1px solid rgba(0, 0, 0, 0.23)",
    borderRadius: "4px",
    boxShadow: "none",
    backgroundColor: "#f6f6f6",
    "&:hover": {
      borderColor: "rgba(0, 0, 0, 0.87)",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 10px",
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 1300,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "rgba(0, 0, 0, 0.6)",
    fontSize: "16px",
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: "16px",
    color: "rgba(0, 0, 0, 0.87)",
  }),
};

const MenuList = memo((props) => {
  const height = 35;
  const { options, children, maxHeight, getValue } = props;
  const [value] = getValue();
  const initialOffset = options.indexOf(value) * height;

  return (
    <List
      height={Math.min(maxHeight, options.length * height)}
      itemCount={children.length}
      itemSize={height}
      initialScrollOffset={initialOffset}
    >
      {({ index, style }) => <div style={style}>{children[index]}</div>}
    </List>
  );
});

const DropdownWithCreatableSelect = ({
  options,
  value,
  onChange,
  placeholder,
}) => (
  <Box sx={{ ".MuiOutlinedInput-root": { padding: "0 10px" } }}>
    <CreatableSelect
      isClearable
      options={options}
      components={{ MenuList }}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      styles={customStyles}
    />
  </Box>
);

function LawFirmLocationDetail() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [locationData, setLocationData] = useState({});
  const [allCities, setAllCities] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [allCounties, setAllCounties] = useState([]);
  const [loadingCitiesStates, setLoadingCitiesStates] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false); // State for submit loading
  const { role } = useSelector((state) => state.auth);

  useEffect(() => {
    const fetchLocationDetails = async () => {
      setLoadingCitiesStates(true);
      try {
        const data = await apiClient(`/lawfirm-location/${id}`, "GET");
        setLocationData(data);
      } catch (error) {
        console.error("Error fetching location data:", error);
      } finally {
        setLoadingCitiesStates(false);
      }
    };
    fetchLocationDetails();
  }, [id]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const cities = await apiClient("/fields/city/", "GET");
        const states = await apiClient("/fields/states/", "GET");
        const counties = await apiClient("/fields/counties/", "GET");
        setAllCities(cities);
        setAllStates(states);
        setAllCounties(counties);
      } catch (error) {
        console.error("Error loading cities/states/counties:", error);
      } finally {
        setLoadingCitiesStates(false);
      }
    };
    loadData();
  }, []);

  const handleFieldChange = (field, value) => {
    setLocationData((prevData) => ({
      ...prevData,
      [field]: value === "" && field === "customer_number" ? null : value,
    }));
  };

  const handleGoogleCallback = ({ address, city, state, county, zipCode }) => {
    setLocationData((prevData) => ({
      ...prevData,
      address,
      city,
      state: normalizeUsState(state, allStates),
      county,
      zip_code: zipCode,
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true); // Start loading
    try {
      const normalizedState = normalizeUsState(locationData.state, allStates);
      if (normalizedState && !isAllowedUsState(normalizedState, allStates)) {
        toast.error(`Invalid state "${locationData.state}". Please select a valid state.`);
        return;
      }

      const payload = {
        ...locationData,
        state: normalizedState,
        is_physical_address: locationData.is_physical_address || false,
        is_mailing_address: locationData.is_mailing_address || false,
        is_billing_address: locationData.is_billing_address || false,
      };

      await apiClient(`/lawfirm-location/${id}/`, "PATCH", payload);
      toast.success("Location updated successfully!");
      navigate(-1); // Navigate back after successful submission
    } catch (error) {
      console.error("Error updating location:", error);
      toast.error("Failed to save changes. Please try again.");
    } finally {
      setIsSubmitting(false); // Stop loading
    }
  };

  if (loadingCitiesStates) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Navbar />
      <Container maxWidth="md" sx={{ marginTop: 4 }}>
        <Paper
          sx={{ padding: 4, backgroundColor: "#f5f5f5", borderRadius: "15px" }}
        >
          <Typography
            variant="h4"
            style={{ fontWeight: "bold", marginBottom: "20px" }}
          >
            Law Firm Location Details
          </Typography>

          <Grid container spacing={3}>
            {/* Location Name */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Location Name"
                fullWidth
                variant="outlined"
                value={locationData.name || ""}
                onChange={(e) => handleFieldChange("name", e.target.value)}
              />
            </Grid>

            {/* Address Search Field */}
            <Grid size={{ xs: 12 }}>
              <GoogleAutoCompleteField
                label="Search Address"
                placeholder="Search for an address (e.g., 600 Brickell Ave)"
                callback={handleGoogleCallback}
                callbackObject
                clearOnSelect
                searchIconPosition="end"
                disableUntilLoaded
                searchIconButtonDisabled
              />
            </Grid>

            {/* Manual Address Field */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Address"
                fullWidth
                variant="outlined"
                value={locationData.address || ""}
                onChange={(e) => handleFieldChange("address", e.target.value)}
              />
            </Grid>

            {/* Zip Code */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Zip Code"
                fullWidth
                variant="outlined"
                value={locationData.zip_code || ""}
                onChange={(e) => handleFieldChange("zip_code", e.target.value)}
              />
            </Grid>

            {/* City */}
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <FormLabel>City</FormLabel>
                <DropdownWithCreatableSelect
                  options={allCities.map((city) => ({
                    label: city,
                    value: city,
                  }))}
                  value={
                    locationData.city
                      ? { label: locationData.city, value: locationData.city }
                      : null
                  }
                  onChange={(opt) =>
                    handleFieldChange("city", opt ? opt.value : "")
                  }
                  placeholder="Select or type a city"
                />
              </FormControl>
            </Grid>

            {/* State */}
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <FormLabel>State</FormLabel>
                <DropdownWithCreatableSelect
                  options={allStates.map((state) => ({
                    label: state,
                    value: state,
                  }))}
                  value={
                    locationData.state
                      ? { label: locationData.state, value: locationData.state }
                      : null
                  }
                  onChange={(opt) => {
                    const next = normalizeUsState(opt ? opt.value : "", allStates);
                    handleFieldChange("state", next);
                  }}
                  placeholder="Select or type a state"
                />
              </FormControl>
            </Grid>

            {/* County */}
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <FormLabel>County</FormLabel>
                <DropdownWithCreatableSelect
                  options={allCounties.map((county) => ({
                    label: county,
                    value: county,
                  }))}
                  value={
                    locationData.county
                      ? {
                        label: locationData.county,
                        value: locationData.county,
                      }
                      : null
                  }
                  onChange={(opt) =>
                    handleFieldChange("county", opt ? opt.value : "")
                  }
                  placeholder="Select or type a county"
                />
              </FormControl>
            </Grid>

            {/* Phone */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Phone"
                fullWidth
                variant="outlined"
                value={locationData.phone || ""}
                onChange={(e) => handleFieldChange("phone", e.target.value)}
                slotProps={{ input: {
                  inputComponent: InputMask,
                  inputProps: {
                    mask: "999-999-9999",
                    maskChar: "_",
                  },
                }}}
              />
            </Grid>

            {/* Fax */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Fax"
                fullWidth
                variant="outlined"
                value={locationData.fax || ""}
                onChange={(e) => handleFieldChange("fax", e.target.value)}
              />
            </Grid>

            {/* Customer Number (Conditional) */}
            {role !== "law_firm_admin" && role !== "law_firm_user" && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Customer Number"
                  fullWidth
                  variant="outlined"
                  value={locationData.customer_number || ""}
                  onChange={(e) =>
                    handleFieldChange("customer_number", e.target.value)
                  }
                />
              </Grid>
            )}

            {/* Checkboxes */}
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      locationData.use_separate_firm_accounting_contact || false
                    }
                    onChange={() =>
                      handleFieldChange(
                        "use_separate_firm_accounting_contact",
                        !locationData.use_separate_firm_accounting_contact
                      )
                    }
                  />
                }
                label="Use separate accounting contact"
              />
            </Grid>

            {/* Accounting Contact Fields (Conditional) */}
            {locationData.use_separate_firm_accounting_contact && (
              <>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Accounting Contact Name"
                    fullWidth
                    variant="outlined"
                    value={locationData.accounting_contact_full_name || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "accounting_contact_full_name",
                        e.target.value
                      )
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Accounting Contact Email"
                    fullWidth
                    variant="outlined"
                    value={locationData.accounting_contact_email || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "accounting_contact_email",
                        e.target.value
                      )
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Accounting Contact Phone"
                    fullWidth
                    variant="outlined"
                    value={locationData.accounting_contact_phone || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "accounting_contact_phone",
                        e.target.value
                      )
                    }
                  />
                </Grid>
              </>
            )}

            {/* Address Type Checkboxes */}
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={locationData.is_physical_address || false}
                    onChange={() =>
                      handleFieldChange(
                        "is_physical_address",
                        !locationData.is_physical_address
                      )
                    }
                  />
                }
                label="Physical Address"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={locationData.is_mailing_address || false}
                    onChange={() =>
                      handleFieldChange(
                        "is_mailing_address",
                        !locationData.is_mailing_address
                      )
                    }
                  />
                }
                label="Mailing Address"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={locationData.is_billing_address || false}
                    onChange={() =>
                      handleFieldChange(
                        "is_billing_address",
                        !locationData.is_billing_address
                      )
                    }
                  />
                }
                label="Billing Address"
              />
            </Grid>

            {/* Submit Button */}
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 3,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Submit"
                  )}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </>
  );
}

export default LawFirmLocationDetail;
